import Constants from 'expo-constants';

import { apiService } from '@/services/api-service';
import type { ApiErrorKind } from '@/utils/api-error';
import { logger } from '@/utils/logger';

export interface BugReportInput {
	/** Logical flow the failure happened in, e.g. 'association-save'. */
	area: string;
	/** Sub-step that failed, e.g. 'late-fees'. */
	section?: string;
	errorKind?: ApiErrorKind;
	httpStatus?: number;
	message: string;
	path?: string;
	associationId?: string;
	/** Anything extra worth capturing for triage. */
	context?: Record<string, unknown>;
}

const getClientVersion = (): string => String(Constants.expoConfig?.version || 'unknown');

const getUserAgent = (): string | undefined =>
	typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

/**
 * File a bug report to the centralized /bug-reports endpoint. Best-effort: the
 * caller surfaces a confirmation/failure to the user, so this resolves with the
 * created id or rejects with the underlying error.
 */
export const reportBug = async (input: BugReportInput): Promise<{ id: string; createdAt: string }> => {
	const payload = {
		...input,
		clientVersion: getClientVersion(),
		userAgent: getUserAgent(),
	};
	// Log identifiers only — the underlying failure was already logged with its
	// message at the point of failure (see runWithReauth), and the full payload
	// carries the server response body + UA we don't want in production logs.
	logger.warn('[bug-report] filing', { area: input.area, section: input.section, errorKind: input.errorKind, httpStatus: input.httpStatus });
	return apiService.fetchApi('/bug-reports', {
		method: 'POST',
		body: JSON.stringify(payload),
	});
};
